import { useEffect, useRef, useState } from "react";
import { usePrava } from "@/api/hooks/useAuth";
import { VETA } from "@/lib/prava";
import { Link, useSearchParams } from "react-router-dom";
import { OperacieSection } from "@/components/cennik/OperacieSection";
import { BoardThicknessesSection } from "@/components/nastavenia/BoardThicknessesSection";
import {
  DodavateliaSection,
  UpravaCienSection,
} from "@/components/nastavenia/CennikyDodavatelovSection";
import { CutritePomocnikSection } from "@/components/nastavenia/CutritePomocnikSection";
import { PilaOdrezkySection } from "@/components/nastavenia/PilaOdrezkySection";
import { EshopSablonySection } from "@/components/nastavenia/EshopSablonySection";
import {
  CncStrojSection,
  DphSection,
  MaxHrubkaDielcaSection,
  VlastnaPolozkaSection,
  NadmieraHranySection,
  NarezovyPlanSection,
  PorezTabulSection,
  PredfrezSection,
  ZvyskyPorezuSection,
} from "@/components/nastavenia/GlobalneSekcie";
import { HranovePravidlaSection } from "@/components/nastavenia/HranovePravidlaSection";
import {
  KATEGORIE,
  kategoriaZAdresy,
  type KategoriaNastaveni,
} from "@/components/nastavenia/kategorie";
import { NaucenePriradeniaSection } from "@/components/nastavenia/NaucenePriradeniaSection";
import { NazvyDielcovSection } from "@/components/nastavenia/NazvyDielcovSection";
import { PracovneDoskySection } from "@/components/nastavenia/PracovneDoskySection";
import { PreferovaniDodavateliaSection } from "@/components/nastavenia/PreferovaniDodavateliaSection";
import { SplitRulesSection } from "@/components/nastavenia/SplitRulesSection";
import { ZhodaCutRiteSection } from "@/components/nastavenia/ZhodaCutRiteSection";
import { ZostavyKlucSection } from "@/components/nastavenia/ZostavyKlucSection";
import { cn } from "@/lib/utils";

/**
 * Nastavenia — sedem kategórií v ľavom menu (KOLO 170).
 *
 * Do KOLA 170 bolo dvadsať kariet pod sebou a user: *„musím skrolovať
 * úplne dole, aby som sa k dačomu dostal"*. Kategória je v adrese
 * (`?kategoria=`, `components/nastavenia/kategorie.ts`), takže odkaz
 * z inej časti appky otvorí rovno správne miesto.
 *
 * Kategória sa pripojí, až keď ju user otvorí (nenačítava sa nič, čo
 * nevidí), a potom ostane pripojená skrytá — rozpísaná hodnota karty bez
 * uloženia tak prežije preklik na inú kategóriu a späť.
 */
export function NastaveniaPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const aktivna = kategoriaZAdresy(params.get("kategoria"));
  const [navstivene, setNavstivene] = useState<ReadonlySet<KategoriaNastaveni>>(
    () => new Set([aktivna]),
  );
  const obsahRef = useRef<HTMLDivElement>(null);
  const predosla = useRef(aktivna);

  useEffect(() => {
    setNavstivene((prev) => (prev.has(aktivna) ? prev : new Set(prev).add(aktivna)));
    // Prepnutie (menu aj odkaz v karte) ukáže kategóriu od začiatku —
    // skroluje `main` v AppShell, nie okno
    if (predosla.current !== aktivna) {
      predosla.current = aktivna;
      obsahRef.current?.scrollIntoView({ block: "start" });
    }
  }, [aktivna]);

  function prepni(kategoria: KategoriaNastaveni): void {
    const next = new URLSearchParams(params);
    next.set("kategoria", kategoria);
    setParams(next, { replace: true });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl">Nastavenia</h1>
        <p className="text-sm text-muted-foreground">
          Vyber kategóriu — každá karta sa ukladá svojím tlačidlom Uložiť.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
        <nav
          aria-label="Kategórie nastavení"
          className="md:sticky md:top-0 md:self-start"
        >
          <ul className="flex flex-wrap gap-1 md:flex-col">
            {KATEGORIE.map((k) => (
              <li key={k.kluc}>
                <button
                  type="button"
                  aria-current={k.kluc === aktivna ? "page" : undefined}
                  onClick={() => prepni(k.kluc)}
                  className={cn(
                    "w-full rounded-md border px-3 py-2 text-left text-sm transition-colors",
                    k.kluc === aktivna
                      ? "border-primary bg-background-alt font-medium"
                      : "border-transparent hover:bg-background-alt/60",
                  )}
                >
                  {k.nazov}
                  <span className="hidden text-[11px] font-normal text-muted-foreground md:block">
                    {k.popis}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div ref={obsahRef} className="min-w-0 scroll-mt-4">
          {KATEGORIE.map((k) =>
            navstivene.has(k.kluc) ? (
              <section
                key={k.kluc}
                aria-label={k.nazov}
                hidden={k.kluc !== aktivna}
                className="space-y-6"
              >
                {OBSAH[k.kluc]}
              </section>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Nastavenia výroby upravuje rozpočtár (KOLO 231) — obchodník ich vidí
 * len na čítanie: `fieldset disabled` vypne polia aj tlačidlá kariet.
 */
function LenVyroba({ children }: { children: React.ReactNode }): JSX.Element {
  const prava = usePrava();
  if (prava.nastavenia_vyroby) return <>{children}</>;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{VETA.vyroba}</p>
      <fieldset disabled className="min-w-0 space-y-6">
        {children}
      </fieldset>
    </div>
  );
}

/** Karta s odkazmi na nastavenia, ktoré žijú v Cenníku. */
function VCenniku(): JSX.Element {
  return (
    <section className="space-y-2 rounded-lg border bg-card p-4">
      <h2 className="text-xl">Marže a štruktúry</h2>
      <p className="text-xs text-muted-foreground">
        Práca, réžia a nadmiera podľa kategórie materiálu a katalóg
        štruktúr povrchu sa nastavujú v Cenníku — pri položkách, ktorých
        sa týkajú.
      </p>
      <div className="flex flex-wrap gap-4 text-sm">
        <Link to="/cennik?pohlad=nastavenia-cien" className="text-primary underline">
          Nastavenia cien (marže kategórií)
        </Link>
        <Link to="/cennik?pohlad=struktury" className="text-primary underline">
          Štruktúry povrchu
        </Link>
      </div>
    </section>
  );
}

/** Obsah kategórie — poradie kariet je poradie v kategórii. */
const OBSAH: Record<KategoriaNastaveni, JSX.Element> = {
  ceny: (
    <>
      {/* Cenníky dodávateľov a úprava cien idú po riadkoch — tovar
          obchodník, ostatné rozpočtár (KOLO 231) */}
      <LenVyroba>
        <DphSection />
        <VlastnaPolozkaSection />
      </LenVyroba>
      <DodavateliaSection />
      <UpravaCienSection />
      <LenVyroba>
        <PreferovaniDodavateliaSection />
        <EshopSablonySection />
      </LenVyroba>
      <VCenniku />
    </>
  ),
  hrany: (
    <LenVyroba>
      <NadmieraHranySection />
      <PredfrezSection />
      <HranovePravidlaSection />
    </LenVyroba>
  ),
  dielce: (
    <LenVyroba>
      {/* Hrúbky odkazujú na lepené dielce „nižšie“ — musia ísť pred nimi */}
      <BoardThicknessesSection />
      <SplitRulesSection />
      <NazvyDielcovSection />
    </LenVyroba>
  ),
  vyroba: (
    <LenVyroba>
      <OperacieSection />
      <CncStrojSection />
    </LenVyroba>
  ),
  rezanie: (
    <LenVyroba>
      <PorezTabulSection />
      <ZhodaCutRiteSection />
      <ZvyskyPorezuSection />
      <NarezovyPlanSection />
    </LenVyroba>
  ),
  topsolid: (
    <LenVyroba>
      <NaucenePriradeniaSection />
      <PracovneDoskySection />
      <MaxHrubkaDielcaSection />
    </LenVyroba>
  ),
  prepojenia: (
    <>
      {/* Pomocník sa pýta na kľúč „zhora“ — kľúč musí ísť prvý */}
      <ZostavyKlucSection />
      <CutritePomocnikSection />
      <PilaOdrezkySection />
    </>
  ),
};
